import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, Minus, Trash2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { gsap } from 'gsap';

const CartSidebar: React.FC = () => {
  const { cart, isOpen, setIsOpen, removeFromCart, updateQuantity } = useCart();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Инициализация состояния при первом открытии
  useEffect(() => {
    if (isOpen && !isVisible) {
      setIsVisible(true);
    }
  }, [isOpen, isVisible]);

  useEffect(() => {
    if (!sidebarRef.current || !overlayRef.current) return;

    if (isOpen) {
      gsap.set([sidebarRef.current, overlayRef.current], { willChange: 'transform, opacity' });
      gsap.set(sidebarRef.current, { x: '100%' });
      gsap.set(overlayRef.current, { opacity: 0 });
      
      gsap.to(sidebarRef.current, {
        x: '0%',
        duration: 0.3,
        ease: 'power2.out'
      });
      
      gsap.to(overlayRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => { 
          if (sidebarRef.current && overlayRef.current) {
            gsap.set([sidebarRef.current, overlayRef.current], { willChange: 'auto' });
          }
        }
      });
    } else {
      gsap.set([sidebarRef.current, overlayRef.current], { willChange: 'transform, opacity' });
      
      gsap.to(sidebarRef.current, {
        x: '100%',
        duration: 0.3,
        ease: 'power2.in'
      });
      
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => { 
          if (sidebarRef.current && overlayRef.current) {
            gsap.set([sidebarRef.current, overlayRef.current], { willChange: 'auto' });
            setIsVisible(false);
          }
        }
      });
    }
  }, [isOpen]);

  const handleQuantityChange = (productId: string, variantId: string | undefined, newQuantity: number) => {
    if (newQuantity > 0) {
      updateQuantity(productId, variantId, newQuantity);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={() => setIsOpen(false)}
      />
      
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white dark:bg-gray-800 shadow-2xl z-50 overflow-hidden"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Корзина
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.items.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-gray-400">Ваша корзина пуста</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.items.map((item) => {
                  // Получаем текущую цену и изображение в зависимости от варианта
                  const currentPrice = item.variantId ? 
                    item.product.variants?.find(v => v.id === item.variantId)?.price || item.product.price || 0 :
                    item.product.price || 0;
                  const currentImage = item.variantId ? 
                    item.product.variants?.find(v => v.id === item.variantId)?.image || item.product.image :
                    item.product.image;
                  const currentWeight = item.variantId ? 
                    item.product.variants?.find(v => v.id === item.variantId)?.weight || '' :
                    '';
                  
                  return (
                    <div key={`${item.product.id}-${item.variantId || 'default'}`} className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <img
                        src={currentImage}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {item.product.name}
                        </h3>
                        {currentWeight && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Вес: {currentWeight}
                          </p>
                        )}
                        {currentPrice > 0 && (
                          <p className="text-primary-600 dark:text-primary-400 font-semibold">
                            {formatPrice(currentPrice)} ₽
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuantityChange(item.product.id, item.variantId, item.quantity - 1)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center text-gray-900 dark:text-white">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.product.id, item.variantId, item.quantity + 1)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id, item.variantId)}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {cart.items.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  Итого:
                </span>
                <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                  {formatPrice(cart.total)} ₽
                </span>
              </div>
              <button 
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/checkout';
                }}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Оформить заказ
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
